import { Fragment, ReactNode } from "react";

import { LendingVaultExpanded, UserVaultPositionExpanded } from "@bridgesplit/abf-react";
import { AppDialog, useAppDialog } from "app/utils";
import { getLendingVaultPath } from "app/components/common";
import { useNavigate } from "react-router-dom";
import { Button, MenuItems, PopoverWrapper, Row, SPACING, usePopover } from "@bridgesplit/ui";
import { AutoAwesomeOutlined, ExpandMore, VisibilityOutlined } from "@mui/icons-material";

import { VaultExternalRewardsClaimCta } from "../external-rewards";

type VaultAction = {
    label: string;
    component: ReactNode;
    icon?: ReactNode;
    hide?: boolean;
    hideDialogHeader?: boolean;
    onClick?: () => void; //Callback for when the button is a MenuItem
};

export function VaultActions({
    vaultPosition,
    unclaimedVaultRewards,
    isCondensed,
    maxItems = 2
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    unclaimedVaultRewards: boolean;
    isCondensed: boolean;
    maxItems?: number;
}) {
    const maxButtons = isCondensed ? 1 : maxItems;
    const fullWidth = isCondensed ? true : false;
    const ctas = useVaultActions({ vaultPosition, unclaimedVaultRewards, fullWidth });
    const { open: openPopover, popoverProps, close } = usePopover();

    if (!ctas?.length) return null;

    const visibleCtas = ctas.slice(0, maxButtons);
    const extraCtas = ctas.slice(maxButtons);

    return (
        <Row spacing={1}>
            {visibleCtas.map((cta, i) => (
                <Fragment key={`visible-cta-${i}`}>{cta.component}</Fragment>
            ))}
            {extraCtas.length === 1 && <Fragment key="extra-cta">{extraCtas[0].component}</Fragment>}
            {extraCtas.length > 1 && (
                <Fragment key="extra-ctas">
                    <Button onClick={openPopover} variant="outlined" fullWidth={fullWidth}>
                        More <ExpandMore />
                    </Button>
                    <PopoverWrapper
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ horizontal: "left", vertical: -SPACING }}
                        {...popoverProps}
                    >
                        <MenuItems
                            close={close}
                            items={extraCtas.map((cta, i) => ({
                                text: cta.label,
                                icon: cta.icon,
                                onClick: () => {
                                    close();
                                    cta.onClick?.();
                                }
                            }))}
                        />
                    </PopoverWrapper>
                </Fragment>
            )}
        </Row>
    );
}

export function useVaultActions({
    vaultPosition,
    unclaimedVaultRewards,
    fullWidth
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    unclaimedVaultRewards: boolean;
    fullWidth: boolean;
}): VaultAction[] {
    const actions: VaultAction[] = [];
    const { open } = useAppDialog();
    const navigate = useNavigate();

    if (!vaultPosition) return actions;

    if (vaultPosition.active) {
        actions.push({
            label: "Deposit/Withdraw",
            component: <VaultDepositWithdrawCta vaultExpanded={vaultPosition.vaultExpanded} fullWidth={fullWidth} />,
            onClick: () =>
                open(AppDialog.VaultManageEscrow, { vaultExpanded: vaultPosition.vaultExpanded, side: "deposit" })
        });
    }

    if (unclaimedVaultRewards) {
        actions.push({
            label: "Claim rewards",
            component: (
                <VaultExternalRewardsClaimCta vaultExpanded={vaultPosition.vaultExpanded} fullWidth={fullWidth} />
            ),
            icon: <AutoAwesomeOutlined />,
            onClick: () => open(AppDialog.VaultClaimRewards, { vaultExpanded: vaultPosition.vaultExpanded })
        });
    }
    actions.push({
        label: "View vault",
        component: <ViewVaultCta vaultExpanded={vaultPosition.vaultExpanded} fullWidth={fullWidth} />,
        icon: <VisibilityOutlined />,
        onClick: () => navigate(getLendingVaultPath(vaultPosition.vaultExpanded))
    });

    return actions;
}

function VaultDepositWithdrawCta({
    vaultExpanded,
    fullWidth
}: {
    vaultExpanded: LendingVaultExpanded;
    fullWidth: boolean;
}) {
    const { open } = useAppDialog();

    return (
        <Button
            onClick={() => open(AppDialog.VaultManageEscrow, { vaultExpanded, side: "deposit" })}
            variant="contained"
            fullWidth={fullWidth}
        >
            Deposit/Withdraw
        </Button>
    );
}

function ViewVaultCta({ vaultExpanded, fullWidth }: { vaultExpanded: LendingVaultExpanded; fullWidth: boolean }) {
    const navigate = useNavigate();

    return (
        <Button onClick={() => navigate(getLendingVaultPath(vaultExpanded))} variant="outlined" fullWidth={fullWidth}>
            View vault
        </Button>
    );
}
